import {
  bodyMeasurementSchema,
  dailyWeightSchema,
  weeklyCheckinSchema,
  type BodyMeasurement,
  type CheckinDecision,
  type CheckinSource,
  type DailyWeight,
  type WeeklyCheckin,
} from '@cadencia/contracts';
import type { WeightPoint } from '@cadencia/domain';
import { logger } from '../../lib/logger.js';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { newSyncColumns, putRow, type WriteContext } from '../writes.js';
import { parchear } from '../parchear.js';

/**
 * Senales corporales del atleta: peso, perimetros y check-ins semanales.
 *
 * EL PESO LLEGO AQUI EL 21/09/2026, desde `nutritionRepo`. Estaba alli porque
 * sin peso no arrancaba el motor de energia y tuvo que existir en la Fase 4;
 * pero `daily_weights` es una senal corporal del §5 y §10 del encargo, no un
 * dato de nutricion, y dejarla en el repositorio que se retira la habria
 * arrastrado consigo. Sus lectores de hoy son el seguimiento de progreso y la
 * revision de bloque, ninguno nutricional.
 *
 * Como en el resto de repositorios: al leer `safeParse` y se registra lo
 * invalido (ADR-035), al escribir `parse`.
 */

function normalizar<T>(
  filas: readonly unknown[],
  esquema: { safeParse: (value: unknown) => { success: boolean; data?: unknown } },
  tabla: string,
): T[] {
  const salida: T[] = [];
  let invalidas = 0;
  for (const fila of filas) {
    const resultado = esquema.safeParse(fila);
    salida.push((resultado.success ? resultado.data : fila) as T);
    if (!resultado.success) invalidas += 1;
  }
  if (invalidas > 0) {
    logger.warn('db', 'PROGRESS_SHAPE_MISMATCH', 'Filas locales que no encajan con su contrato', {
      tabla,
      invalidas,
    });
  }
  return salida;
}

// --------------------------------------------------------------------------
// Peso corporal
// --------------------------------------------------------------------------

export async function listWeights(userId: string, sinceDate?: string): Promise<DailyWeight[]> {
  const filas = await db.dailyWeights.where('userId').equals(userId).toArray();
  const vivas = filas.filter(
    (fila) => fila.deletedAt === null && (sinceDate === undefined || fila.measuredOn >= sinceDate),
  );
  return normalizar<DailyWeight>(vivas, dailyWeightSchema, 'dailyWeights').sort((a, b) =>
    a.measuredOn.localeCompare(b.measuredOn),
  );
}

/** Los pesos en la forma que espera `weightTrend`, ordenados de antiguo a nuevo. */
export async function weightPoints(userId: string, sinceDate?: string): Promise<WeightPoint[]> {
  const pesos = await listWeights(userId, sinceDate);
  return pesos.map((peso) => ({ measuredOn: peso.measuredOn, weightKg: peso.weightKg }));
}

/** La pesada mas reciente. */
export async function latestWeight(userId: string): Promise<DailyWeight | undefined> {
  const pesos = await listWeights(userId);
  return pesos[pesos.length - 1];
}

export interface DailyWeightInput {
  measuredOn: string;
  weightKg: number;
  bodyFatPct: number | null;
  conditionsStandard: boolean;
  note: string | null;
}

/**
 * Anota el peso de un dia.
 *
 * Un dia, una fila: apuntar dos veces el peso del martes es corregirlo, no
 * pesarse dos veces (ADR-038). Si fueran dos filas, la media movil contaria el
 * martes doble y la tendencia saldria sesgada.
 */
export async function saveWeight(
  ctx: WriteContext,
  input: DailyWeightInput,
): Promise<DailyWeight> {
  const existentes = await listWeights(ctx.userId, input.measuredOn);
  const previo = existentes.find((fila) => fila.measuredOn === input.measuredOn);
  const row =
    previo === undefined
      ? dailyWeightSchema.parse({ ...newSyncColumns(ctx), ...input })
      : parchear(dailyWeightSchema, previo, input, { tabla: 'daily_weights', id: previo.id });
  return putRow(ctx, 'dailyWeights', row);
}

// --------------------------------------------------------------------------
// Perimetros
// --------------------------------------------------------------------------

export async function listMeasurements(
  userId: string,
  sinceDate?: string,
): Promise<BodyMeasurement[]> {
  const filas = await db.bodyMeasurements.where('userId').equals(userId).toArray();
  const vivas = filas.filter(
    (fila) => fila.deletedAt === null && (sinceDate === undefined || fila.measuredOn >= sinceDate),
  );
  return normalizar<BodyMeasurement>(vivas, bodyMeasurementSchema, 'bodyMeasurements').sort((a, b) =>
    a.measuredOn.localeCompare(b.measuredOn),
  );
}

export async function getMeasurement(
  userId: string,
  measuredOn: string,
): Promise<BodyMeasurement | undefined> {
  return (await listMeasurements(userId)).find((fila) => fila.measuredOn === measuredOn);
}

export interface MeasurementInput {
  measuredOn: string;
  waistCm: number | null;
  chestCm: number | null;
  armCm: number | null;
  thighCm: number | null;
  calfCm: number | null;
  side: BodyMeasurement['side'];
  note: string | null;
}

/**
 * Una medicion por dia.
 *
 * Corregir la cinta de ayer es reescribir esa fila, no anadir una segunda que
 * nadie sabria cual manda. El indice unico de la 0019 lo impone tambien en el
 * servidor.
 */
export async function saveMeasurement(
  ctx: WriteContext,
  input: MeasurementInput,
): Promise<BodyMeasurement> {
  const previo = await getMeasurement(ctx.userId, input.measuredOn);
  const row =
    previo === undefined
      ? bodyMeasurementSchema.parse({ ...newSyncColumns(ctx), ...input })
      : parchear(bodyMeasurementSchema, previo, input, {
          tabla: 'body_measurements',
          id: previo.id,
        });
  return putRow(ctx, 'bodyMeasurements', row);
}

export async function removeMeasurement(
  ctx: WriteContext,
  fila: BodyMeasurement,
): Promise<void> {
  await putRow(ctx, 'bodyMeasurements', { ...fila, deletedAt: nowIso() });
}

/**
 * La ultima medicion con cintura, que es la que cruza el arbol del §9.
 *
 * No vale la ultima fila sin mas: quien apunto solo el brazo la semana pasada
 * tiene una fila mas reciente y ninguna cintura nueva.
 *
 * `hasta` acota «ultima» a un dia. Quien decide una fase para el 24 de agosto
 * no puede usar la cintura del 30, y las filas llegan por sincronizacion con la
 * fecha del otro dispositivo, asi que el caso no es hipotetico. Descartar la
 * medicion posterior no puede tirar tambien la buena: se filtra ANTES de tomar
 * la ultima, no despues.
 */
export async function latestWaist(
  userId: string,
  hasta?: string,
): Promise<BodyMeasurement | undefined> {
  const conCintura = (await listMeasurements(userId)).filter(
    (fila) => fila.waistCm !== null && (hasta === undefined || fila.measuredOn <= hasta),
  );
  return conCintura[conCintura.length - 1];
}

// --------------------------------------------------------------------------
// Check-in semanal
// --------------------------------------------------------------------------

export async function listCheckins(userId: string): Promise<WeeklyCheckin[]> {
  const filas = await db.weeklyCheckins.where('userId').equals(userId).toArray();
  const vivas = filas.filter((fila) => fila.deletedAt === null);
  return normalizar<WeeklyCheckin>(vivas, weeklyCheckinSchema, 'weeklyCheckins').sort((a, b) =>
    b.weekStart.localeCompare(a.weekStart),
  );
}

export async function getCheckin(
  userId: string,
  weekStart: string,
): Promise<WeeklyCheckin | undefined> {
  return (await listCheckins(userId)).find((fila) => fila.weekStart === weekStart);
}

export interface CheckinInput {
  weekStart: string;
  metrics: Record<string, unknown>;
  summary: string;
  interpretation: string;
  decision: CheckinDecision;
  changes: Record<string, unknown>[];
  source?: CheckinSource;
}

/**
 * Guarda el check-in de una semana, reescribiendo el que hubiera.
 *
 * Una semana, una fila. Volver a ejecutar la revision de la semana 12 con mas
 * datos tiene que corregir esa fila, no anadir una segunda: dos check-ins de la
 * misma semana obligarian a decidir cual cuenta, y la respuesta siempre seria
 * «el ultimo».
 */
export async function saveCheckin(
  ctx: WriteContext,
  input: CheckinInput,
): Promise<WeeklyCheckin> {
  const previo = await getCheckin(ctx.userId, input.weekStart);
  const row =
    previo === undefined
      ? weeklyCheckinSchema.parse({ ...newSyncColumns(ctx), source: 'algorithm', ...input })
      : parchear(weeklyCheckinSchema, previo, input, {
          tabla: 'weekly_checkins',
          id: previo.id,
        });
  return putRow(ctx, 'weeklyCheckins', row);
}
